
import React from 'react';

interface RatingBar {
  star: number;
  count: number;
}

interface ReviewSummaryProps {
  averageRating: number;
  totalReviews: number;
  distribution: RatingBar[];
  activeFilter: number | null;
  onFilterChange: (rating: number) => void;
}

export default function ReviewSummary({
  averageRating,
  totalReviews,
  distribution,
  activeFilter,
  onFilterChange,
}: ReviewSummaryProps) {
  // Guard against empty distribution array
  const safeDistribution = Array.isArray(distribution) ? distribution : [];
  const maxCount = Math.max(...safeDistribution.map((d) => d.count), 1);

  const renderStars = (rating: number) => {
    const stars = [];
    const full = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    for (let i = 0; i < full; i++) {
      stars.push(
        <i
          key={`f-${i}`}
          className="ri-star-fill text-[#2E7D32]"
        ></i>
      );
    }
    if (hasHalf) {
      stars.push(
        <i key="h" className="ri-star-half-fill text-[#2E7D32]"></i>
      );
    }
    for (let i = 0; i < 5 - full - (hasHalf ? 1 : 0); i++) {
      stars.push(
        <i
          key={`e-${i}`}
          className="ri-star-line text-gray-300"
        ></i>
      );
    }
    return stars;
  };

  const handleClearFilter = () => {
    if (activeFilter !== null) {
      onFilterChange(activeFilter);
    }
  };

  return (
    <div className="bg-[#F9FBF7] rounded-xl p-6 sticky top-28">
      {/* Average */}
      <div className="text-center mb-6 pb-6 border-b border-gray-200">
        <p className="text-5xl font-serif text-gray-900 mb-2">
          {averageRating.toFixed(1)}
        </p>
        <div className="flex items-center justify-center gap-1 text-sm mb-2">
          {renderStars(averageRating)}
        </div>
        <p className="text-sm text-gray-500">
          Based on {totalReviews} reviews
        </p>
      </div>

      {/* Distribution Bars */}
      <div className="space-y-2.5">
        {safeDistribution.map(({ star, count }) => {
          const pct = totalReviews > 0 ? (count / maxCount) * 100 : 0;
          const isActive = activeFilter === star;
          return (
            <button
              key={star}
              onClick={() => onFilterChange(star)}
              className={`w-full flex items-center gap-3 py-1.5 px-2 rounded-md transition-all cursor-pointer ${
                isActive ? 'bg-[#E8F5E9]' : 'hover:bg-gray-100'
              }`}
            >
              <span className="text-xs font-medium text-gray-700 w-3 shrink-0">
                {star}
              </span>
              <i className="ri-star-fill text-[#2E7D32] text-xs"></i>
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#2E7D32] rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 w-6 text-right shrink-0">
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {activeFilter && (
        <button
          onClick={handleClearFilter}
          className="mt-4 w-full text-center text-xs text-[#2E7D32] font-medium hover:underline cursor-pointer"
        >
          Clear filter
        </button>
      )}
    </div>
  );
}
